<template>
  <div class="money-going-out-widget-container">
    <div v-if="loading" class="money-going-out-widget-loading">Loading...</div>
    <div v-else-if="error" class="money-going-out-widget-error">{{ error }}</div>
    <div v-else class="money-going-out-widget-content">
      <div class="money-going-out-widget-header">
        <div class="money-going-out-widget-header-label">Total Amount</div>
        <div class="money-going-out-widget-header-value money-going-out-widget-negative">{{ formatCurrency(totalBillAmount) }}</div>
      </div>
      <div class="money-going-out-widget-details">
        <div class="money-going-out-widget-detail-row">
          <div class="money-going-out-widget-detail-label">Bill Entered</div>
          <div class="money-going-out-widget-detail-value">{{ formatCurrency(totalBillAmount) }}</div>
        </div>
        <div class="money-going-out-widget-detail-row">
          <div class="money-going-out-widget-detail-label">Amount Paid</div>
          <div class="money-going-out-widget-detail-value money-going-out-widget-paid">{{ formatCurrency(amountPaid) }}</div>
        </div>
        <div class="money-going-out-widget-detail-row">
          <div class="money-going-out-widget-detail-label">Amount Pending</div>
          <div class="money-going-out-widget-detail-value money-going-out-widget-pending">{{ formatCurrency(amountPending) }}</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import './styles/MoneyGoingOutWidget.css';
import ApiService from '@/services/api.service';
import logger from '@/utils/logger';

export default {
  name: 'MoneyGoingOutWidget',
  props: {
    companyId: { type: [String, Number], required: true }
  },
  data() {
    return {
      totalBillAmount: 0,
      amountPaid: 0,
      amountPending: 0,
      loading: true,
      error: null
    };
  },
  async mounted() {
    await this.fetchData();
  },
  methods: {
    async fetchData() {
      this.loading = true;
      this.error = null;
      try {
        // Fetch all bills (not expenses) to show bill payment details
        const billsData = await ApiService.getBillsAndExpenses(this.companyId, { view: 'Bills' });
        const billsArray = Array.isArray(billsData) ? billsData : [];
        
        // Filter to only include bills (not expenses) and exclude voided/cancelled
        const validBills = billsArray.filter(bill => {
          return bill.type === 'Bill' &&
                 !bill.voided &&
                 bill.status !== 'voided' &&
                 bill.status !== 'cancelled';
        });
        
        // Calculate totals
        this.totalBillAmount = validBills.reduce((sum, bill) => {
          return sum + parseFloat(bill.originalAmount || bill.totalAmount || 0);
        }, 0);
        
        // Calculate amount paid (from paidAmount field or calculated from remainingAmount)
        this.amountPaid = validBills.reduce((sum, bill) => {
          const totalAmount = parseFloat(bill.originalAmount || bill.totalAmount || 0);
          const remainingAmount = parseFloat(bill.remainingAmount || bill.balance || 0);
          const paidAmount = totalAmount - remainingAmount;
          return sum + Math.max(0, paidAmount);
        }, 0);
        
        // Calculate amount pending (remaining amount due)
        this.amountPending = validBills.reduce((sum, bill) => {
          return sum + parseFloat(bill.remainingAmount || bill.balance || 0);
        }, 0);
      } catch (error) {
        logger.error('Error fetching money going out:', error);
        this.error = 'Failed to load data';
      } finally {
        this.loading = false;
      }
    },
    formatCurrency(amount) {
      if (amount === null || amount === undefined || isNaN(amount)) return '$0.00';
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(amount);
    }
  }
};
</script>

